import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { roleHistory } from '../data/experience';
import { ExperienceGraph } from './ExperienceGraph';
import { SectionLabel } from './SectionLabel';
const skills = ['Visual Design', 'UX Design', 'Prototyping', 'UX Research', 'User Testing', 'Interaction / Motion', 'Front-end', 'UX Writing'];
const tools = ['Figma', 'Sketch', 'Framer', 'Principle', 'Notion', 'Linear', 'Webflow'];
const education = [{
  degree: 'BFA, Graphic Design',
  school: 'University of South Florida',
  year: '2007 — 2011'
}];
const reveal = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function About() {
  const reduceMotion = useReducedMotion();
  const [photoFailed, setPhotoFailed] = useState(false);
  return <section id="about" className="site-container py-24 md:py-32" data-id="element-15" data-mp="3mj2gantf44p9">
      <div className="border-t border-border pt-5" data-id="element-16" data-mp="3rgaxom6qs4pg">
        <SectionLabel data-id="element-17" data-mp="3req1yt8oyfq0">About</SectionLabel>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-14 lg:mt-14 lg:grid-cols-12 lg:gap-10" data-id="element-18" data-mp="2lrh6jojlw360">
        <aside className="lg:col-span-4" data-id="element-19" data-mp="2grmhlqje5nnl">
          <div className="lg:sticky lg:top-28" data-id="element-20" data-mp="2gqt6yehbae06">
            <h2 className="max-w-sm text-balance text-4xl font-medium leading-[1.03] tracking-[-0.035em] md:text-5xl" data-id="element-21" data-mp="2cq0wxj0m852u">
              Thoughtful design starts with better questions.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-muted" data-id="element-22" data-mp="34i8dykk19jhr">
              I believe great design solves real problems elegantly—without
              drawing unnecessary attention to itself.
            </p>
            <div className="mt-10 aspect-square w-full overflow-hidden bg-border" data-id="element-23" data-mp="2vod8tciwvt24">
              {photoFailed ? <div className="flex h-full w-full items-center justify-center font-display text-4xl font-medium uppercase tracking-[0.08em] text-muted" role="img" aria-label="Alexander Kusminov" data-id="element-24" data-mp="2qpsic4tgonqv">
                  AK
                </div> : <img src="https://cdn.magicpatterns.com/uploads/g2ouv8uBuAvsReMFYbVXsw/image.png" alt="Alexander Kusminov" onError={() => setPhotoFailed(true)} className="aspect-square w-full object-cover grayscale" data-id="element-25" data-mp="686gawosysuk" />}
            </div>
            <p className="mt-4 font-display text-sm font-medium uppercase tracking-[0.14em] text-muted" data-id="element-26" data-mp="3jkguawc83v1d">
              Currently residing in St. Petersburg, FL
            </p>
          </div>
        </aside>

        <div className="space-y-20 lg:col-span-7 lg:col-start-6" data-id="element-27" data-mp="3al3l84s0h8h6">
          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-28" data-mp="3eldp9bw8tote">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-29" data-mp="1crxzeh7zcdzh">
              Background
            </h3>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-muted" data-id="element-30" data-mp="2tbr93jml0hgl">
              <p data-id="element-31" data-mp="372nzr4wy6ol0">
                With over a decade of experience in the design industry, I've
                had the privilege of working with startups and enterprise
                companies alike, helping them translate complex business
                requirements into intuitive, user-centric digital products.
              </p>
              <p data-id="element-32" data-mp="3c0pghj6wibtb">
                My approach is deeply rooted in minimalism and functionalism. I
                strip away the superfluous to uncover the core value of a
                product, ensuring every pixel serves a purpose. Whether it's a
                dense data dashboard or a consumer mobile app, my goal is to
                create clarity out of chaos.
              </p>
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-33" data-mp="1qq8stf4rq9ti">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-34" data-mp="2214udth3a6p8">
              Experience by industry
            </h3>
            <div className="mt-8" data-id="element-35" data-mp="19fleuzpgel4g">
              <ExperienceGraph data-id="element-36" data-mp="19ch4tlpb4v5v" />
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-37" data-mp="1eeesqt8nke55">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-38" data-mp="qn13iw5ssyoe">
              Experience
            </h3>
            <div className="mt-8 space-y-12" data-id="element-39" data-mp="vksspif266ow">
              {roleHistory.map(company => <div key={company.company} data-id="element-40" data-mp="o1jt2gwctmd1">
                  <div className="mb-5 flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-6" data-id="element-41" data-mp="j49zqq5ugqjx">
                    <h4 className="break-words text-2xl font-medium leading-tight tracking-[-0.02em]" data-id="element-42" data-mp="e45ekmtzr7nd">
                      {company.company}
                    </h4>
                    <span className="shrink-0 font-display text-sm font-medium uppercase tracking-[0.08em] text-muted" data-id="element-43" data-mp="55ab3iic0bnr">
                      ~{company.years} {company.years === 1 ? 'yr' : 'yrs'}
                    </span>
                  </div>
                  <ul className="space-y-3 border-l border-border pl-4 md:pl-5" data-id="element-44" data-mp="3rkmm3ncjwzyn">
                    {company.roles.map(role => <li key={role.title} className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:justify-between md:gap-6" data-id="element-45" data-mp="3hm5pa9qxlr4o">
                        <span className="break-words text-base text-foreground" data-id="element-46" data-mp="32zq8rbjb4kuh">{role.title}</span>
                        <span className="shrink-0 text-sm text-muted" data-id="element-47" data-mp="37ybd9n5ldqoq">{role.range}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-48" data-mp="2yyo94tlha6rz">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-49" data-mp="3cwl6vtbo4j88">
              Education
            </h3>
            <div className="mt-8" data-id="element-50" data-mp="3humrwjsnmsmc">
              {education.map(item => <div key={item.degree} className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between md:gap-6" data-id="element-51" data-mp="9fcon8d13hcz">
                  <div data-id="element-52" data-mp="e9gwruy15vfn">
                    <h4 className="text-xl font-medium" data-id="element-53" data-mp="nlxvespkngzq">{item.degree}</h4>
                    <p className="mt-1 text-sm text-muted" data-id="element-54" data-mp="jkuhwcuzxx1o">{item.school}</p>
                  </div>
                  <span className="shrink-0 font-display text-sm font-medium uppercase tracking-[0.08em] text-muted" data-id="element-55" data-mp="xc9drka05sq5">
                    {item.year}
                  </span>
                </div>)}
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} className="grid gap-12 md:grid-cols-2" data-id="element-56" data-mp="4od8hr6dashr">
            <div data-id="element-57" data-mp="9p18h2ckqjo4">
              <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-58" data-mp="1q0oer2wauaih">Capabilities</h3>
              <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 text-sm text-muted" data-id="element-59" data-mp="1l2muwfr1u75y">
                {skills.map(skill => <li key={skill} data-id="element-60" data-mp="3fjd6qfr7zej9">{skill}</li>)}
              </ul>
            </div>
            <div data-id="element-61" data-mp="3jivs1cmydrmm">
              <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-62" data-mp="3jkgleyxj74iw">Tools</h3>
              <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-3 text-sm text-muted" data-id="element-63" data-mp="3ohfede5gl5fi">
                {tools.map(tool => <li key={tool} data-id="element-64" data-mp="3tbjmw1udhlip">{tool}</li>)}
              </ul>
            </div>
          </motion.section>
        </div>
      </div>
    </section>;
}